import { usePortalUiLanguage } from "./PortalUiLanguageContext"
import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import api from '../services/api'
import { storage } from '../utils/storage'
import { normalizedPositionCanTgMonitoring } from '../monitoring/monitoringTgAccess'
import { normalizedPositionCanFleetAiAnalytics } from '../monitoring/fleetAiAccess'
import { employeeHomeHref } from '../utils/employeeDeskPath'
import { employeeHasCreativePortalAccess } from '../utils/creativePortalAccess'

import type { EmployeeWorkspace } from '../utils/employeeWorkspace'

interface User {
  workspace?: EmployeeWorkspace
  id: string
  name: string
  role: 'admin' | 'employee'
  employee_id?: string
  /** Из cookie session (Zam, Manager, …) — см. /api/auth/me */
  normalized_position?: string | null
  /** Все совмещённые должности; первая остаётся основной для стартового экрана. */
  normalized_positions?: string[]
  /** Канонический путь главной: /employee/desk/<slug>/<id> */
  desk_path?: string | null
  /** Zam/Control + флаг в карточке — панель постановки /admin/task-2-0 */
  task_board_v2_assigner?: boolean
  /** Галочка в карточке сотрудника — КреоПортал /admin/creative-calendar */
  creative_portal_access?: boolean
  /** Сервер: /api/auth/me — доступ к /employer/system-ai-analytics (зам по роли или флаг для Manager/OpDir) */
  can_access_fleet_ai_analytics?: boolean
  desk_client_show_prices?: boolean
  desk_prefs?: Record<string, boolean> | null
  /** Язык интерфейса из профиля сотрудника. */
  language?: 'ru' | 'en' | 'id'
  /** Серверный capability contract; UI не должен заново угадывать права по должности. */
  permissions?: string[]
  /** Серверные runtime feature flags; отсутствие флага закрывает функцию. */
  feature_flags?: { meta_ai?: boolean }
}

/** Результат POST /api/auth/login для admin с нового IP (нужен второй шаг). */
export type AdminSecondFactor = 'telegram' | 'totp'
export type LoginResult = { status: 'ok' } | { status: 'admin_otp'; challengeId: string; factors: AdminSecondFactor[]; detail: string }

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<LoginResult>
  verifyAdminIpOtp: (challengeId: string, codes: { telegramCode?: string; totpCode?: string }) => Promise<void>
  logout: () => void
  isAuthenticated: boolean
  isAdmin: boolean
  /** Админ или руководящая должность с доступом к /employee/monitoring_tg */
  canAccessTgMonitoring: boolean
  /** Админ или Zam — дашборд /admin/system-ai-analytics */
  canAccessFleetAiAnalytics: boolean
  /** Админ или сотрудник с флагом постановки (Zam/Control + карточка) — /admin/task-2-0 */
  canAccessTaskBoardV2: boolean
  /** Админ или галочка «КреоПортал» в карточке сотрудника */
  canAccessCreativePortal: boolean
  hasPermission: (permission: string) => boolean
}

const AUTH_TOKEN_KEY = 'auth_token'
const AUTH_USER_KEY = 'auth_user'

function postLoginHref(user: User): string {
  if (window.location.hostname.toLowerCase() === 'office.alex-digital.io') return '/office'
  return user.role === 'admin' ? '/home' : employeeHomeHref(user)
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Функция для декодирования JWT токена (без проверки подписи на клиенте)
function decodeJWT(token: string): any | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = parts[1]
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
    return decoded
  } catch (error) {
    return null
  }
}

function buildUserFromDecoded(decoded: any): User | null {
  if (!decoded) return null
  return {
    id: decoded.role === 'admin' ? 'admin' : decoded.employee_id || decoded.portal_user_id || '',
    name: decoded.name || (decoded.role === 'admin' ? 'Admin' : 'Сотрудник'),
    role: decoded.role === 'admin' ? 'admin' : 'employee',
    employee_id: decoded.employee_id,
    normalized_position: decoded.normalized_position ?? null,
    normalized_positions: Array.isArray(decoded.normalized_positions)
      ? decoded.normalized_positions.map(String)
      : undefined,
    desk_path: decoded.desk_path ?? null,
    task_board_v2_assigner: Boolean(decoded.task_board_v2_assigner),
    creative_portal_access: Boolean(decoded.creative_portal_access),
    can_access_fleet_ai_analytics: decoded.can_access_fleet_ai_analytics,
    desk_client_show_prices: decoded.desk_client_show_prices,
    permissions: Array.isArray(decoded.permissions) ? decoded.permissions.map(String) : undefined,
  }
}

function employeePermissionOrLegacy(
  user: User | null,
  permission: string,
  legacy: boolean,
): boolean {
  if (!user || user.role !== 'employee') return false
  if (Array.isArray(user.permissions)) return user.permissions.includes(permission)
  return legacy
}

function legacyReadOnlyAdsPermission(user: User | null, permission: string): boolean {
  if (!user || user.role !== 'employee') return false
  const position = String(user.normalized_position || '').trim().toLocaleLowerCase('ru-RU')
  if (permission === 'google.metrics.view') {
    return ['google', 'control', 'контроллер', 'контроль', 'zam', 'зам', 'заместитель'].includes(position)
  }
  if (permission === 'facebook.metrics.view') {
    return ['facebook', 'meta', 'control', 'контроллер', 'контроль', 'zam', 'зам', 'заместитель'].includes(position)
  }
  return false
}
function isTokenExpired(token: string): boolean {
  const decoded = decodeJWT(token)
  if (!decoded || !decoded.exp) return true
  const exp = decoded.exp * 1000 // конвертируем в миллисекунды
  return Date.now() >= exp
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const { bindAccountLanguage } = usePortalUiLanguage()
  useEffect(() => {
    if (loading) return
    if (!user) { bindAccountLanguage(''); return }
    const positions = [user.normalized_position, ...(user.normalized_positions || [])]
    const fallback = user.role === 'admin' || positions.includes('Zam') ? 'ru' : 'en'
    bindAccountLanguage(`${user.role}:${user.employee_id || user.id}`, user.language || fallback)
  }, [loading, user?.role, user?.employee_id, user?.id, user?.language, user?.normalized_position, bindAccountLanguage])
  const hasPermission = (permission: string): boolean => {
    if (user?.role === 'admin') return true
    return Boolean(
      user?.permissions?.includes(permission) || legacyReadOnlyAdsPermission(user, permission),
    )
  }

  useEffect(() => {
    // Проверяем токен из localStorage при загрузке
    checkAuthFromToken()
  }, [])

  const checkAuthFromToken = async () => {
    try {
      let token = storage.get<string>(AUTH_TOKEN_KEY)
      if (token && isTokenExpired(token)) {
        storage.remove(AUTH_TOKEN_KEY)
        storage.remove(AUTH_USER_KEY)
        token = null
      }

      // 1) Сессия из HTML-логина: cookie `session` (и при необходимости JWT в заголовке).
      // Эндпоинт /api/auth/me сначала читает session, потом Bearer — см. api/routers/auth.py
      try {
        const response = await api.get('/api/auth/me')
        if (response.data?.user) {
          const u = response.data.user as User
          setUser(u)
          storage.set(AUTH_USER_KEY, u)
          return
        }
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status === 401) {
          storage.remove(AUTH_TOKEN_KEY)
          storage.remove(AUTH_USER_KEY)
          setUser(null)
        } else {
          const cached = storage.get<User>(AUTH_USER_KEY)
          if (cached) setUser(cached)
          else setUser(null)
        }
        return
      }

      // 2) Только JWT (SPA-логин), если сессии нет, но токен валиден
      token = storage.get<string>(AUTH_TOKEN_KEY)
      if (token && !isTokenExpired(token)) {
        const decoded = decodeJWT(token)
        const userData = buildUserFromDecoded(decoded)
        if (userData) {
          setUser(userData)
          storage.set(AUTH_USER_KEY, userData)
          return
        }
      }
    } catch {
      storage.remove(AUTH_TOKEN_KEY)
      storage.remove(AUTH_USER_KEY)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  const completeAccessTokenLogin = async (token: string) => {
    storage.set(AUTH_TOKEN_KEY, token)
    const userData = buildUserFromDecoded(decodeJWT(token))
    if (!userData) {
      throw new Error('Failed to decode token')
    }
    storage.set(AUTH_USER_KEY, userData)
    setUser(userData)
    window.location.href = postLoginHref(userData)
  }

  const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
      const response = await api.post('/api/auth/login', { login: email, password })
      const d = response.data as {
        admin_otp_required?: boolean
        challenge_id?: string
        required_factors?: AdminSecondFactor[]
        detail?: string
        access_token?: string
      }
      if (d?.admin_otp_required && d?.challenge_id) {
        return { status: 'admin_otp', challengeId: String(d.challenge_id), factors: d.required_factors || ['telegram'], detail: d.detail || 'Введите код подтверждения' }
      }
      if (d?.access_token) {
        await completeAccessTokenLogin(d.access_token)
        return { status: 'ok' }
      }
      const fallbackResponse = await api.post('/auth/login', { login: email, password })
      const fd = fallbackResponse.data as {
        admin_otp_required?: boolean
        challenge_id?: string
        required_factors?: AdminSecondFactor[]
        user?: User
        detail?: string
      }
      if (fd?.admin_otp_required && fd?.challenge_id) {
        return { status: 'admin_otp', challengeId: String(fd.challenge_id), factors: fd.required_factors || ['telegram'], detail: fd.detail || 'Введите код подтверждения' }
      }
      if (fd?.user) {
        const userData = fd.user
        storage.set(AUTH_USER_KEY, userData)
        setUser(userData)
        window.location.href = postLoginHref(userData)
        return { status: 'ok' }
      }
      throw new Error(typeof fd?.detail === 'string' ? fd.detail : 'Login failed')
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      throw new Error(typeof detail === 'string' ? detail : 'Login failed')
    }
  }

  const verifyAdminIpOtp = async (challengeId: string, codes: { telegramCode?: string; totpCode?: string }) => {
    try {
      const response = await api.post('/api/auth/admin-ip-otp', {
        challenge_id: challengeId,
        telegram_code: (codes.telegramCode || '').trim(),
        totp_code: (codes.totpCode || '').trim(),
      })
      const token = (response.data as { access_token?: string })?.access_token
      if (!token) {
        const detail = (response.data as { detail?: string })?.detail
        throw new Error(typeof detail === 'string' ? detail : 'OTP failed')
      }
      await completeAccessTokenLogin(token)
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      throw new Error(typeof detail === 'string' ? detail : 'Неверный код или истёк срок')
    }
  }

  const logout = async () => {
    try {
      await api.post('/auth/logout')
    } catch (error) {
      // Ignore errors on logout
    } finally {
      // Очищаем localStorage
      storage.remove(AUTH_TOKEN_KEY)
      storage.remove(AUTH_USER_KEY)
      setUser(null)
      window.location.href = '/login'
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        verifyAdminIpOtp,
        logout,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        canAccessTgMonitoring:
          user?.role === 'admin' ||
          employeePermissionOrLegacy(
            user,
            'monitoring.telegram',
            normalizedPositionCanTgMonitoring(user?.normalized_position),
          ),
        canAccessFleetAiAnalytics:
          user?.role === 'admin' ||
          [user?.normalized_position, ...(user?.normalized_positions || [])].some(
            normalizedPositionCanFleetAiAnalytics,
          ) ||
          employeePermissionOrLegacy(
            user,
            'analytics.fleet_ai',
            Boolean(user?.can_access_fleet_ai_analytics),
          ),
        canAccessTaskBoardV2:
          user?.role === 'admin' ||
          employeePermissionOrLegacy(
            user,
            'task.assign',
            Boolean(user?.task_board_v2_assigner),
          ),
        canAccessCreativePortal:
          user?.role === 'admin' ||
          employeePermissionOrLegacy(
            user,
            'creative.portal',
            user
              ? employeeHasCreativePortalAccess({
                  role: user.role,
                  creative_portal_access: user.creative_portal_access,
                  normalized_position: user.normalized_position,
                })
              : false,
          ),
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
